import { implementsTKeys, strEnum } from "../../types";
import * as lodash from "lodash";
import { ITCITenantAppCategory, TCITenantAppType } from "./tci-application";
import { ITCITaggable } from "./tci-app-tags";
import { ITCILike, ITCILikeAble, ITCIRateAble, ITCIRatingComment } from "./tci-ratings";
import {
    AbstractTCITenantComponentOptions,
    ITCIOptionType,
    ITCITenantComponentOptions,
    ITCITenantModalOptions,
    ITCITenantModalType,
    TCITenantModalType,
    TCITenantSelectorType
} from "./tci-tenant";
import { ITCIMediaAsset } from "./tci-media";
import { Observable } from "rxjs/Observable";
import { ITCITenantComponentInput } from "./tci-component";
import { ITCIAssetRef } from "./tci-assets";


export const TCI_MARKETPLACE_ORG_TYPE = strEnum(["None", "Provider", "Listing", "Consumer"]);
export type TCI_MARKETPLACE_ORG_TYPE = keyof typeof TCI_MARKETPLACE_ORG_TYPE;

export const TCI_MARKETPLACE_LISTING_TYPE = strEnum(["Connector", "Application", "Accelerator"]);
export type TCI_MARKETPLACE_LISTING_TYPE = keyof typeof TCI_MARKETPLACE_LISTING_TYPE;

export const TCI_MARKETPLACE_LISTING_STATE = strEnum(["Draft", "Published", "Unknown"]);
export type TCI_MARKETPLACE_LISTING_STATE = keyof typeof TCI_MARKETPLACE_LISTING_STATE;

export const TCI_MARKETPLACE_ACTIONS = strEnum([
    "PromoteVisibilityPrivate",
    "PromoteVisibilityPublic",
    "WithdrawListing",
    "DeleteListing",
    "SubmitForApproval",
    "ApproveRequest", // APPROVE_REQUEST
    "RejectRequest", // REJECT_REQUEST
    "RevokeAccess", // REVOKE_ACCESS
    "UnRevokeAccess", // UNREVOKE_ACCESS
    "RequestAccess", // REQUEST
    "GetAccess", // GET
    "Uninstall", // UNINSTALL
    "Install", // UNINSTALL
    "AcknowledgeRevokeAccess", // ACKNOWLEDGE_REVOKED
    "AcknowledgeReject" // ACKNOWLEDGE_REJECT
]);
export type TCI_MARKETPLACE_ACTIONS = keyof typeof TCI_MARKETPLACE_ACTIONS;

export enum TCI_MARKETPLACE_PUBLISH_TEXT {
    PUBLISH_PRIVATELY = "Publish privately",
    UPDATE_LISTING = "Update listing",
    UPGRADE_LISTING = "Upgrade listing",
    SUBMIT_APPROVAL = "Submit changes for approval"
}

export class TCIMarketplaceListingStatus {
    // Provider Org
    public static PENDING_APPROVAL = new TCIMarketplaceListingStatus("PendingApproval");
    public static PRIVATELY_AVAILABLE = new TCIMarketplaceListingStatus("PrivatelyAvailable");
    public static PUBLICLY_AVAILABLE = new TCIMarketplaceListingStatus("PubliclyAvailable");
    // Consuming Org
    public static LISTING_AVAILABLE = new TCIMarketplaceListingStatus("ListingAvailable");
    public static REQUEST_APPROVED = new TCIMarketplaceListingStatus("RequestApproved");
    public static REQUEST_REJECTED = new TCIMarketplaceListingStatus("RequestRejected");
    public static LISTING_ENABLED = new TCIMarketplaceListingStatus("ListingEnabled");
    public static LISTING_DISABLED = new TCIMarketplaceListingStatus("ListingDisabled");
    public static LISTING_REQUEST = new TCIMarketplaceListingStatus("ListingRequest");
    public static PENDING_REQUEST = new TCIMarketplaceListingStatus("PendingRequest");
    public static LISTING_GET = new TCIMarketplaceListingStatus("ListingGet");
    public static ACCESS_REVOKED = new TCIMarketplaceListingStatus("AccessRevoked");
    public static ACCESS_REVOKE_ACKNOWLEDGED = new TCIMarketplaceListingStatus("AccessRevokeAcknowledged");
    // Listing Org
    public static LISTING_OVERRIDE = new TCIMarketplaceListingStatus("ListingOverride");
    public static UNKNOWN = new TCIMarketplaceListingStatus("UNKNOWN");
    public static all = () => TCIMarketplaceListingStatus.createPseudoListingStatus(...TCIMarketplaceListingStatus.getAllStatusTypes());

    public static createPseudoListingStatus(...stattuses: TCIMarketplaceListingStatus[]): TCIMarketplaceListingStatus {
        if (!stattuses || stattuses.length === 0) {
            throw Error("Needs Tenant Type arguments");
        }
        const pseudoName = stattuses && stattuses
            .sort((a, b) => {
                if (b.toString() > a.toString()) {
                    return 1;
                } else if (b.toString() === a.toString()) {
                    return 0;
                } else {
                    return -1;
                }
            })
            .map(status => status.toString()).join(",");
        if (stattuses.length === 1) {
            return stattuses[0];
        } else {
            const types = TCIMarketplaceListingStatus.getAllStatusTypes().filter((type) => type.val === pseudoName);
            if (types.length > 0) {
                throw Error("Cannot use existing app types");
            }
            return new TCIMarketplaceListingStatus(pseudoName);
        }
    }

    /**
     * Parse JSON object to StatusType
     * @param jsonObj
     */
    public static fromJSON(jsonObj: any): TCIMarketplaceListingStatus {
        interface StatusType {
            val: string;
        }

        if (implementsTKeys<StatusType>(jsonObj, ["val"])) {
            return TCIMarketplaceListingStatus.fromString(jsonObj.val);
        }

    }

    /**
     * Return recognized app type, it does not mean the
     * app type is valid in TCI uses
     * @param v
     */
    public static fromString(v: string): TCIMarketplaceListingStatus {
        switch (v) {
            case TCIMarketplaceListingStatus.PENDING_APPROVAL.toString():
                return TCIMarketplaceListingStatus.PENDING_APPROVAL;
            case TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE.toString():
                return TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE;
            case TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE.toString():
                return TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE;
            case TCIMarketplaceListingStatus.REQUEST_APPROVED.toString():
                return TCIMarketplaceListingStatus.REQUEST_APPROVED;
            case TCIMarketplaceListingStatus.REQUEST_REJECTED.toString():
                return TCIMarketplaceListingStatus.REQUEST_REJECTED;
            case TCIMarketplaceListingStatus.LISTING_AVAILABLE.toString():
                return TCIMarketplaceListingStatus.LISTING_AVAILABLE;
            case TCIMarketplaceListingStatus.LISTING_REQUEST.toString():
                return TCIMarketplaceListingStatus.LISTING_REQUEST;
            case TCIMarketplaceListingStatus.PENDING_REQUEST.toString():
                return TCIMarketplaceListingStatus.PENDING_REQUEST;
            case TCIMarketplaceListingStatus.LISTING_ENABLED.toString():
                return TCIMarketplaceListingStatus.LISTING_ENABLED;
            case TCIMarketplaceListingStatus.LISTING_DISABLED.toString():
                return TCIMarketplaceListingStatus.LISTING_DISABLED;
            case TCIMarketplaceListingStatus.LISTING_GET.toString():
                return TCIMarketplaceListingStatus.LISTING_GET;
            case TCIMarketplaceListingStatus.ACCESS_REVOKED.toString():
                return TCIMarketplaceListingStatus.ACCESS_REVOKED;
            case TCIMarketplaceListingStatus.ACCESS_REVOKE_ACKNOWLEDGED.toString():
                return TCIMarketplaceListingStatus.ACCESS_REVOKE_ACKNOWLEDGED;
            case TCIMarketplaceListingStatus.LISTING_OVERRIDE.toString():
                return TCIMarketplaceListingStatus.LISTING_OVERRIDE;
            default:
                return TCIMarketplaceListingStatus.UNKNOWN;
        }
    }

    /**
     * Get Marketplace listing state
     * @param v
     */
    public static getListingState(v: TCIMarketplaceListingStatus): TCI_MARKETPLACE_LISTING_STATE {
        switch (v.val) {
            case TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE.toString():
                return TCI_MARKETPLACE_LISTING_STATE.Draft;
            /**
             * Consuming Org Statuses cannot be used to determine the
             * publishing State
             */
            case TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE.toString():
                // case TCIMarketplaceListingStatus.REQUEST_APPROVED.toString():
                // case TCIMarketplaceListingStatus.REQUEST_REJECTED.toString():
                // case TCIMarketplaceListingStatus.LISTING_AVAILABLE.toString():
                // case TCIMarketplaceListingStatus.LISTING_REQUEST.toString():
                // case TCIMarketplaceListingStatus.LISTING_ENABLED.toString():
                // case TCIMarketplaceListingStatus.LISTING_GET.toString():
                // case TCIMarketplaceListingStatus.ACCESS_REVOKED.toString():
                // case TCIMarketplaceListingStatus.LISTING_OVERRIDE.toString():
                return TCI_MARKETPLACE_LISTING_STATE.Published;
            default:
                return TCI_MARKETPLACE_LISTING_STATE.Unknown;
        }
    }

    /**
     * Determine which Org the status belongs to as these statuses are not common between orgs
     * @param v
     */
    public static getListingOrgType(v: TCIMarketplaceListingStatus): TCI_MARKETPLACE_ORG_TYPE {
        switch (v.val) {
            case TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE.toString():
            case TCIMarketplaceListingStatus.PENDING_APPROVAL.toString():
            case TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE.toString():
                return TCI_MARKETPLACE_ORG_TYPE.Provider;
            case TCIMarketplaceListingStatus.REQUEST_APPROVED.toString():
            case TCIMarketplaceListingStatus.REQUEST_REJECTED.toString():
            case TCIMarketplaceListingStatus.LISTING_AVAILABLE.toString():
            case TCIMarketplaceListingStatus.LISTING_REQUEST.toString():
            case TCIMarketplaceListingStatus.PENDING_REQUEST.toString():
            case TCIMarketplaceListingStatus.LISTING_ENABLED.toString():
            case TCIMarketplaceListingStatus.LISTING_DISABLED.toString():
            case TCIMarketplaceListingStatus.LISTING_GET.toString():
            case TCIMarketplaceListingStatus.ACCESS_REVOKED.toString():
            case TCIMarketplaceListingStatus.ACCESS_REVOKE_ACKNOWLEDGED.toString():
                return TCI_MARKETPLACE_ORG_TYPE.Consumer;
            case TCIMarketplaceListingStatus.LISTING_OVERRIDE.toString():
                return TCI_MARKETPLACE_ORG_TYPE.Listing;
            default:
                return TCI_MARKETPLACE_ORG_TYPE.None;
        }
    }

    /**
     * Get allowed actions
     * @param v
     */
    // public static getAllowedActions(v: TCIMarketplaceListingStatus): TCI_MARKETPLACE_ACTIONS[] {
    //     switch (v.toString()) {
    //         case TCIMarketplaceListingStatus.NEW_UPDATE.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.EditDraft,
    //                 TCI_MARKETPLACE_ACTIONS.DeleteDraft,
    //                 TCI_MARKETPLACE_ACTIONS.PublishPrivate,
    //             ];
    //         case TCIMarketplaceListingStatus.UNDER_REVIEW.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.EditDraft,
    //                 TCI_MARKETPLACE_ACTIONS.DeleteDraft,
    //                 TCI_MARKETPLACE_ACTIONS.PublishPrivate,
    //                 TCI_MARKETPLACE_ACTIONS.ManageAccessForOrgs
    //             ];
    //         case TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.SubmitForPublicListing,
    //                 TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPublic,
    //                 TCI_MARKETPLACE_ACTIONS.ManageAccessForOrgs
    //             ];
    //         case TCIMarketplaceListingStatus.PENDING_APPROVAL.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.Withdraw,
    //                 TCI_MARKETPLACE_ACTIONS.ManageAccessForOrgs
    //             ];
    //         case TCIMarketplaceListingStatus.REJECTED_PUBLIC_LISTING.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.EditDraft,
    //                 TCI_MARKETPLACE_ACTIONS.Withdraw,
    //                 TCI_MARKETPLACE_ACTIONS.ManageAccessForOrgs
    //             ];
    //         case TCIMarketplaceListingStatus.APPROVED_PUBLIC_LISTING.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.Withdraw,
    //                 TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPublic,
    //             ];
    //         case TCIMarketplaceListingStatus.WITHDRAWN_PENDING_APPROVAL.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.Withdraw,
    //                 TCI_MARKETPLACE_ACTIONS.ManageAccessForOrgs
    //             ];
    //         case TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPrivate,
    //                 TCI_MARKETPLACE_ACTIONS.ManageAccessForOrgs
    //             ];
    //         case TCIMarketplaceListingStatus.REQUEST_APPROVED.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.GetEnable
    //             ];
    //         case TCIMarketplaceListingStatus.LISTING_AVAILABLE.toString():
    //             return [
    //                 // TCI_MARKETPLACE_ACTIONS.RequestGetAccess
    //                 // check price
    //                 // if free go to LISTING_GET
    //                 // else check request url
    //                 // if request url == true go to LISTING_REQUEST
    //                 // this is done by internal state actions not by UI action
    //             ];
    //         case TCIMarketplaceListingStatus.LISTING_REQUEST.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.RequestGetAccess
    //             ];
    //         case TCIMarketplaceListingStatus.LISTING_ENABLED.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.Disable
    //             ];
    //         case TCIMarketplaceListingStatus.LISTING_DISABLED.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.GetEnable
    //             ];
    //         case TCIMarketplaceListingStatus.LISTING_GET.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.GetEnable
    //             ];
    //         case TCIMarketplaceListingStatus.ACCESS_REVOKED.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.AcknowledgeRevokeAccess
    //             ];
    //         case TCIMarketplaceListingStatus.ACCESS_REVOKE_ACKNOWLEDGED.toString():
    //             return [
    //                 TCI_MARKETPLACE_ACTIONS.RequestGetAccess
    //             ];
    //         default:
    //             return [];
    //     }
    // }


    /**
     * Only return valid app types that TCI shows on app list
     */
    public static getAllStatusTypes(): TCIMarketplaceListingStatus[] {
        return <TCIMarketplaceListingStatus[]>[
            TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE,
            TCIMarketplaceListingStatus.PENDING_APPROVAL,
            TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE,
            TCIMarketplaceListingStatus.REQUEST_APPROVED,
            TCIMarketplaceListingStatus.REQUEST_REJECTED,
            TCIMarketplaceListingStatus.LISTING_AVAILABLE,
            TCIMarketplaceListingStatus.LISTING_REQUEST,
            TCIMarketplaceListingStatus.PENDING_REQUEST,
            TCIMarketplaceListingStatus.LISTING_ENABLED,
            TCIMarketplaceListingStatus.LISTING_DISABLED,
            TCIMarketplaceListingStatus.LISTING_GET,
            TCIMarketplaceListingStatus.ACCESS_REVOKED,
            TCIMarketplaceListingStatus.ACCESS_REVOKE_ACKNOWLEDGED,
            // TCIMarketplaceListingStatus.LISTING_OVERRIDE,
        ];
    }

    public static is(v1: any, v2: any): boolean {
        return lodash.isEqual(v1, v2);
    }


    private constructor(private val: string) {
    }

    public equals(that: any): boolean {
        // Shortcut for trivial case
        if (this === that) {
            return true;
        }
        // check for primitive
        if (typeof that === "string" && that === this.val) {
            return true;
        }
        // If the that object is not a set, it is not equal to this one.
        // We use instanceof to allow any subclass of Set.
        // We could relax this test if we wanted true duck-typing.
        // Or we could strengthen it to check this.constructor == that.constructor
        // Note that instanceof properly rejects null and undefined values
        if (!(that instanceof TCIMarketplaceListingStatus)) {
            return false;
        }
        // If two sets don't have the same size, they're not equal

        if (this.toString() !== that.toString()) {
            return false;
        }

        return lodash.isEqual(this, that);
    }

    public valueOf(): any {
        return this.val.valueOf();
    }

    public toString(): string {
        return this.val;
    }

}

export const TCI_MARKETPLACE_PRICING_OPTIONS = strEnum(["Paid", "Free"]);
export type TCI_MARKETPLACE_PRICING_OPTIONS = keyof typeof TCI_MARKETPLACE_PRICING_OPTIONS;

/**
 * Pricing Info
 */
interface ITCIMarketplaceListingPricingInfo {
    /**
     * pricing option
     */
    pricingOptions: TCI_MARKETPLACE_PRICING_OPTIONS;
    /**
     * pricing details
     */
    details?: string;
}

/**
 * Listing View Modes
 */
export const TCI_MARKETPLACE_LISTING_VIEW_MODE = strEnum(["LIST_CARD", "LIST_DETAIL", "PROMOTED", "PREVIEW_CARD", "PREVIEW_DETAIL"]);
export type TCI_MARKETPLACE_LISTING_VIEW_MODE = keyof typeof TCI_MARKETPLACE_LISTING_VIEW_MODE;

/**
 * Marketplace Media Embedded component Modes
 */
export const TCI_MARKETPLACE_MEDIA_MODE = strEnum(["VIEW", "EDIT"]);
export type TCI_MARKETPLACE_MEDIA_MODE = keyof typeof TCI_MARKETPLACE_MEDIA_MODE;

/**
 * Listing Detail Enabled/Disabled dropdown options
 */
export const TCI_MARKETPLACE_LISTING_ENABLED_OPTIONS = strEnum(["Enabled", "Disabled"]);
export type TCI_MARKETPLACE_LISTING_ENABLED_OPTIONS = keyof typeof TCI_MARKETPLACE_LISTING_ENABLED_OPTIONS;

/**
 * Listing Editor Modes
 */
export const TCI_MARKETPLACE_LISTING_EDITOR_MODE = strEnum(["CREATE", "EDIT", "UPGRADE", "PENDING_APPROVAL"]);
export type TCI_MARKETPLACE_LISTING_EDITOR_MODE = keyof typeof TCI_MARKETPLACE_LISTING_EDITOR_MODE;
/**
 * Listing Editor Submit Actions
 */
export const TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION = strEnum([
    "CANCEL",
    "UPGRADE",
    "SUBMIT_PRIVATE_NEW",
    "SUBMIT_PRIVATE_UPDATE",
    "SUBMIT_PUBLIC",
]);
export type TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION = keyof typeof TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION;

/**
 * Preview dialog Modes
 */
export const TCI_MARKETPLACE_PREVIEW_DIALOG_MODE = strEnum(["PREVIEW_LISTING", "WITHDRAW_APPROVAL_REQUEST"]);
export type TCI_MARKETPLACE_PREVIEW_DIALOG_MODE = keyof typeof TCI_MARKETPLACE_PREVIEW_DIALOG_MODE;

export interface ITCIMarketplaceListingStatusMap {
    /**
     * key=OrgId
     * This holds listing status for different orgs which are merged
     * e.g. If OrgId == managing Org it the status represents the listing lifecycle status
     * If OrgId == consuming Org then the status represents the listing lifecycle status of the consumer Org
     * This data is merged in this mapping based on information from managing org using its key GSBC Id
     * and then with the logged in user's Org GSBC Id. It is possible that user is from the managing org and
     * also consuming the listing so the status will host two states. Each status state is tied to an org type
     * * e.g {
     *   SUBSCRIBING_ORG_Id : Status && SUBSCRIBING_ORG_Id == logged in user Org Id
     *   PROVIDING_ORG_Id : Status
     *   LISTING_ORG_Id : Status
     *   }
     */
    [OrgId: string]: TCIMarketplaceListingStatus[];
}

/**
 * Mapping between Org type @TCI_MARKETPLACE_ORG_TYPE and Org Id's
 */
export interface ITCIMarketplaceListingOrgTypeMap {
    /**
     * Org Type => Org Id
     */
    [OrgType: string]: string[];
}

export interface ITCIMarketplaceListingOrgTypeStatusMap {
    /**
     * Org Type => Status - only one status per org type
     */
    [OrgType: string]: TCIMarketplaceListingStatus;
}

/**
 * interface ITCIMarketplaceListingMetaData
 * This interface is used for Create New/Edit Listing
 * These are fixed fields which dont change after creation.
 */
export interface ITCIMarketplaceListingMetaData {
    /**
     * unique key uuid to identify a listing
     * listing id created by backend Domain Server, creators
     * should leave it as "" empty string instead of
     * "undefined"
     */
    id: string;
    /**
     * tenant/creator created asset uuid MUST be provided
     * by the creator to check if a previous record exists
     */
    creatorAssetId: string;
    /**
     * Creator is managing/provisioning the asset externally
     */
    externallyManaged?: boolean;
    /**
     * creator ref data which is opaque to TCI
     */
    refData?: any;
    /**
     * name of the item
     */
    name: string;
    /**
     * Listing Type
     * connection/application/accelerator
     */
    listingType: TCI_MARKETPLACE_LISTING_TYPE | string;
    /**
     * TCITenantAppType
     * For accelerators there may not be a tenant type
     * flogo/bw/scribe/node
     */
    tenantType?: TCITenantAppType;
    /**
     * TCITenantCategory
     * For accelerators there may not be a tenant app category
     * Connect/Develop/Integrate
     */
    tenantCategory?: ITCITenantAppCategory;
    /**
     * Version
     */
    version: string;
}

/**
 * ITCIMarketplaceListing
 */
export interface ITCIMarketplaceListing extends ITCITaggable, ITCIRateAble, ITCILikeAble, ITCIMarketplaceListingMetaData {

    /**
     * Display name
     */
    displayName: string;
    /**
     * Description
     */
    description: string;
    /**
     * Display Icon url
     */
    iconUrl: string;
    /**
     * icon file content for saving only
     */
    iconContent?: string;
    /**
     * media assets
     */
    mediaAssets: ITCIMediaAsset[];
    /**
     * additional info url
     */
    infoUrl: string;
    /**
     * EULA url
     */
    eulaUrl: string;
    /**
     * request url
     */
    requestUrl: string;
    /**
     * request approved to do get
     * @deprecated
     */
    requestApproved?: boolean;
    /**
     * Created Date timestamp
     */
    createdDate?: number;
    /**
     * Last Modified timestamp
     */
    lastModifiedDate?: number;
    /**
     * Certification timestamp
     */
    lastCertifiedDate?: number;
    /**
     * if last_modified_dt > last_certified_dt => true else false
     * auto promote to publicly available if not dirty
     */
    dirty: boolean;
    /**
     * show enable/disable
     * @deprecated
     */
    canEnable?: boolean;
    /**
     * enabled state : disable goes to get
     * @deprecated
     */
    enabled?: boolean;
    /**
     * managing org Id
     */
    managingOrgId: string;
    /**
     * managing Org Name (Publisher / Distributer)
     */
    managingOrgName: string;
    /**
     * listing Org/hosting Org
     */
    hostingOrgId: string;
    /**
     * Listing State
     * @deprecated
     */
    listingState?: TCI_MARKETPLACE_LISTING_STATE | string;

    /**
     * Listing Status - orgId/GSBC -> @TCI_MARKETPLACE_ORG_TYPE[]
     * managing org has different status states from the
     * consumer status states.
     * e.g {
     *   SUBSCRIBING_ORG_Id : Status && SUBSCRIBING_ORG_Id == logged in user Org Id
     *   PROVIDING_ORG_Id : Status
     *   LISTING_ORG_Id : Status
     *   }
     */
    listingStatus: ITCIMarketplaceListingStatusMap;
    /**
     * Mapping from Org type to the Org Id's
     * This is needed to know based on the listingStatus above
     * which status belong to the correct org type
     */
    listingOrgMap: ITCIMarketplaceListingOrgTypeMap;
    /**
     * Mapping from Org type to individual status of the org type
     */
    listingOrgTypeStatusMap: ITCIMarketplaceListingOrgTypeStatusMap;
    /**
     * Pricing Info
     */
    pricingInfo: ITCIMarketplaceListingPricingInfo;
    /**
     * Allowed Actions - TBD whether these come from backend or frontend eval
     */
    nextAllowedActions: TCI_MARKETPLACE_ACTIONS[];
    /**
     * eula accepted
     * @deprecated
     */
    eulaAccepted?: boolean;
    /**
     * subscribed by current logged in user org
     * @deprecated
     */
    subscribed?: boolean;
    /**
     * end user sees upgrade available
     */
    hasUpgrade: boolean;
    /**
     * Show upgrade version
     */
    upgradeVersion?: string;
    /**
     * number of pending access requests if the
     * @listingStatus[OrgId == managing Org] == @TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE
     */
    numPendingAccessRequests: number;
    /**
     * purchased by the logged in user Org
     */
    purchasedByUserOrg?: boolean;
    /**
     * Endorsed by TIBCO
     */
    endorsed: boolean;
    /**
     * UI to show enabled without any actions button(Get/Request) if enabledByDefault:true
     * (This is essential for all OOTB bw/flogo connectors now and future)
     */
    enabledByDefault: boolean;
    /**
     * created asset id after GET - mainly for created apps from app templates
     */
    createdAssetId?: string;
    /**
     * user is TIBCO host
     */
    isHostOrgListing?: boolean;
    /**
     * can delete or not
     */
    deletable?: boolean;
    /**
     * Downloadable Assets
     */
    assetRef?: ITCIAssetRef[];
}

export const TCI_LISTING_UPSERT_MODE = strEnum(["create", "update", "upgrade"]);
export type TCI_LISTING_UPSERT_MODE = keyof typeof TCI_LISTING_UPSERT_MODE;

/*************************************************************************************************/
/*                        TCI Marketplace Listing Access                                         */

/*************************************************************************************************/
/**
 * TCI Marketplace Listing Access Data
 * The consumer creates a request
 * The provider approves/rejects/revokes access
 */
export interface ITCIMarketplaceListingAccess {
    /**
     * unique id to locate a access request
     */
    id: string;
    /**
     * listing id
     */
    listingId: string;
    /**
     * Status of an consuming Org for a listing
     */
    consumingOrgStatus?: TCIMarketplaceListingStatus;
    /**
     * provider Org gsbc
     */
    providerGsbc: string;
    /**
     * consuming Org gsbc
     */
    consumingOrgGsbc: string;
    /**
     * consuming Org gsbc hash, the subscription locator
     */
    consumerGsbcHash: string;
    /**
     * consuming Org Name
     */
    consumingOrgName: string;
    /**
     * belongs to which listing item
     * this contains the provider/consumer states
     * <optional>
     */
    listing?: ITCIMarketplaceListing;
    /**
     * Access Request Date
     * consumer
     */
    accessRequestDate?: number;
    /**
     * Access Requested by id ( See UserInfo, need to ask PM if email can be used .. GDPR clearance needed)
     * consumer only
     */
    accessRequestedBy?: string;
    /**
     * requested by email
     */
    accessRequestEmail?: string;
    /**
     * last access Action Date - action can be approve/reject/revoke/un-revoke
     * provider only
     */
    lastAccessUpdateDate?: number;
    /**
     * lastAccess Updated By user id ( See UserInfo, need to ask PM if email can be used .. GDPR clearance needed)
     * provider only
     */
    lastAccessUpdatedBy?: string;
    /**
     * last access action acknowledged date
     * consumer only
     */
    lastAccessAcknowledgedDate?: number;
    /**
     * last access action acknowledged by user id ( See UserInfo, need to ask PM if email can be used .. GDPR clearance needed)
     * consumer only
     */
    lastAccessAcknowledgedBy?: string;
    /**
     * User email who takes managing action
     */
    managingActionUserEmail?: string;
    /**
     * Scribe org id
     */
    scribeOrgId?: string;
    /**
     * The consumer currently in use version, show upgrade if there's new version from provider
     */
    version?: string;

}

/*************************************************************************************************/
/*                        TCI Marketplace Listing Req/Res                                        */

/*************************************************************************************************/
export interface ITCIMarketplaceV2ListingRequest {

    id?: string;
}

export interface ITCIMarketplaceResponse<T> {
    data: T;
    result?: any;
    /**
     *  Response message
     */
    msg?: string;
    /**
     * success or error
     */
    success: boolean;
    /**
     * Error information from tenant server
     */
    error?: { message: string, statusMessage: string };
}

/**
 * Listing response from ws backend
 */
export interface ITCIMarketplaceV2ListingResponse extends ITCIMarketplaceResponse<ITCIMarketplaceListing[]> {
}

/**
 * Listing callback response from ws backend
 */
export interface ITCIMarketplaceV2ListingCallbackResponse extends ITCIMarketplaceResponse<ITCIMarketplaceListingMetaData> {
}

/**
 * RatingComment response from ws backend
 */
export interface ITCIMarketplaceV2RatingResponse extends ITCIMarketplaceResponse<ITCIRatingComment> {
}

/**
 * Like response from ws backend
 */
export interface ITCIMarketplaceV2LikeResponse extends ITCIMarketplaceResponse<ITCILike> {
}

/**
 * Access response from ws backend
 */
export interface ITCIMarketplaceV2AccessResponse extends ITCIMarketplaceResponse<ITCIMarketplaceListingAccess[]> {
}

/**
 * Submit/Withdraw approval response from ws backend
 */
export interface ITCIMarketplaceV2ApprovalResponse extends ITCIMarketplaceResponse<ITCIMarketplaceListing[]> {
}

/*************************************************************************************************/
/*                        TCI Marketplace Listing Action                                        */

/*************************************************************************************************/
/**
 * Action Request
 */
export interface ITCIMarketplaceV2ActionRequest {
    actionType: TCI_MARKETPLACE_ACTIONS;
    listing: ITCIMarketplaceListing;
    subscriptionLocator?: string;
    scribeOrgId?: string;
}

/**
 * Action Response
 */
export interface ITCIMarketplaceV2ActionResponse extends ITCIMarketplaceResponse<ITCIMarketplaceListing[]> {
}

export interface ITCIMarketplaceOrgSubscriptionLocator {
    subscriptionId: string;
    subscriptionLocator: string;
    scribeOrgId: string;
    subscriptionOrgName: string;
}
export interface ITCIMarketplaceOrgSubscriptionLocatorResponse extends ITCIMarketplaceResponse<ITCIMarketplaceOrgSubscriptionLocator> {
}

export type TCI_LISTING_METADATA_CALLBACK = (cinput: ITCIMarketplaceListingMetaData) =>
    Observable<ITCIMarketplaceV2ListingCallbackResponse>;
/*************************************************************************************************/
/*                        TCI Marketplace Listing Service                                        */

/*************************************************************************************************/
export interface ITCIMarketplaceV2ListingService {
    /**
     * Get All Listings from backend
     */
    getAllListings(): Observable<ITCIMarketplaceListing[]>;

    /**
     * Get Listing by Id from Backend
     * @param listingId
     */
    getListingById(listingId: string): Observable<ITCIMarketplaceV2ListingResponse>;

    /**
     * Get Listing by AssetId from Backend
     * @param assetID
     */
    getListingByAssetId(assetId: string): Observable<ITCIMarketplaceV2ListingResponse>;

    /**
     * return true if the listing is in public status by listing or metadata
     * @param metadata
     * @param isMetadata
     */
    isListingPublic(metadata: ITCIMarketplaceListingMetaData | ITCIMarketplaceListing, isMetadata: boolean): Observable<boolean>;

    /**
     * return true if the listing status is present for the org type
     * @param metadata
     * @param orgType
     * @param orgStatus
     * @param isMetadata
     */
    isListingStatusForOrgType(metadata: ITCIMarketplaceListingMetaData | ITCIMarketplaceListing,
        orgType: TCI_MARKETPLACE_ORG_TYPE,
        orgStatus: TCIMarketplaceListingStatus,
        isMetadata: boolean): Observable<boolean>;

    /**
     * Is listing from the logged in user Org
     * @param metadata
     * @param isMetadata
     */
    isListingFromUserOrg(metadata: ITCIMarketplaceListingMetaData | ITCIMarketplaceListing, isMetadata: boolean): Observable<boolean>;

    /**
     * This is an explicit call to trigger a listing metadata refresh  which is expected
     * to do something that changes the marketplace listing menu items
     * @param input
     * @param callback
     */
    pushListingChanges(input: ITCIMarketplaceListingMetaData,
        callback: TCI_LISTING_METADATA_CALLBACK)
        : Observable<ITCIMarketplaceV2ListingCallbackResponse>;


    /**
     * process Action
     * @param req
     * @param body
     */
    processAction(req: ITCIMarketplaceV2ActionRequest, body: any): Observable<ITCIMarketplaceV2ActionResponse>;


    /**
     * Check if the asset listing can be created
     * menuInput cannot be used for extensions as it is tenant specific menu
     * tenant must provide metadata to evaluate version comparision as TCI
     * application does not have version
     * @param opt
     * @param menuInput
     * @param metadata
     */
    canCreateComponentFunction(opt: ITCITenantComponentOptions<any>,
        menuInput: ITCITenantComponentInput,
        metadata: ITCIMarketplaceListingMetaData): Observable<boolean>;

    /**
     * Check if the asset listing can be upgraded
     * menuInput cannot be used for extensions as it is tenant specific menu
     * tenant must provide metadata to evaluate version comparision as TCI
     * application does not have version
     * @param opt
     * @param menuInput
     * @param metadata
     */
    canUpgradeComponentFunction(opt: ITCITenantComponentOptions<any>,
        menuInput: ITCITenantComponentInput,
        metadata: ITCIMarketplaceListingMetaData): Observable<boolean>;


    /**
     * Check if the asset listing can be updated
     * menuInput cannot be used for extensions as it is tenant specific menu
     * tenant must provide metadata to evaluate version comparision as TCI
     * application does not have version
     * @param opt
     * @param menuInput
     * @param metadata
     */
    canUpdateComponentFunction(opt: ITCITenantComponentOptions<any>,
        menuInput: ITCITenantComponentInput,
        metaData: ITCIMarketplaceListingMetaData): Observable<boolean>;

}

/*************************************************************************************************/
/*                        TCI Marketplace Filters                                                */
/*************************************************************************************************/

/**
 * Enumeration of TCI filter Choice enum
 */
export enum TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE {
    ENUM_SINGLE_SELECTION,
    ENUM_MULTI_SELECTION
}


/**
 * Enumeration of TCI filter categories
 */
export class TCIMarketplaceV2FilterCategory {

    private constructor(private id: string, private label: string, private selectType: TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE) {
    }

    public static FILTER_CATEGORY_ASSET_TYPE = new TCIMarketplaceV2FilterCategory(
        "ASSET_TYPE", "Asset Type", TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE.ENUM_SINGLE_SELECTION);
    public static FILTER_CATEGORY_CAPABILITY = new TCIMarketplaceV2FilterCategory(
        "CAPABILITY", "Capability", TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE.ENUM_SINGLE_SELECTION);
    public static FILTER_CATEGORY_SUBSCRIBER = new TCIMarketplaceV2FilterCategory(
        "MORE_FILTERS", "More filters", TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE.ENUM_MULTI_SELECTION);
    public static FILTER_CATEGORY_PROVIDER = new TCIMarketplaceV2FilterCategory(
        "MY_MANAGING_CONTRIBUTIONS", "My Managing Contributions", TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE.ENUM_SINGLE_SELECTION);

    private static allCategories = [
        TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE,
        TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY,
        TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER,
        // TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER,
    ];

    public static getAllCategories(): TCIMarketplaceV2FilterCategory[] {
        return TCIMarketplaceV2FilterCategory.allCategories;
    }

    getId(): string {
        return this.id;
    }

    getLabel(): string {
        return this.label;
    }

    getSelectType(): TCI_MARKETPLACE_LISTING_FILTER_SELECT_TYPE {
        return this.selectType;
    }

}

export interface ITCIMarketplaceV2FiltersCategoryIcon {
    label: string;
    iconUrl: string;
    id: string;
}

export class TciMarketplaceV2Filters {

    // tslint:disable:max-line-length
    public static FILTER_LISTING_TYPE_CONNECTOR = new TciMarketplaceV2Filters("CONNECTOR", "Connector", 0, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE, "ng-ic-asset-type-Connector.svg");
    public static FILTER_LISTING_TYPE_APPLICATION = new TciMarketplaceV2Filters("APPLICATION", "Application", 1, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE, "ng-ic-asset-type-Application.svg");
    public static FILTER_LISTING_TYPE_ACCELERATOR = new TciMarketplaceV2Filters("ACCELERATOR", "Accelerator", 2, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_ASSET_TYPE, "ng-ic-asset-type-Accelerator.svg");
    public static FILTER_CAPABILITY_TYPE_DEVELOP = new TciMarketplaceV2Filters("DEVELOP", "Develop", 0, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY, "ng-app-ic-develop.svg");
    public static FILTER_CAPABILITY_TYPE_CONNECT = new TciMarketplaceV2Filters("CONNECT", "Connect", 1, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY, "ng-app-ic-scribe.svg");
    public static FILTER_CAPABILITY_TYPE_INTEGRATE = new TciMarketplaceV2Filters("INTEGRATE", "Integrate", 2, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY, "ng-app-ic-bw.svg");
    public static FILTER_CAPABILITY_TYPE_OTHER = new TciMarketplaceV2Filters("OTHER", "Other", 3, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_CAPABILITY, "ng-ic-asset-type-Accelerator.svg");
    public static FILTER_SUBSCRIBER_TYPE_ORG = new TciMarketplaceV2Filters("MY_ORG_LISTINGS", "My Org Listings", 0, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER, "ng-ic-filters-my-listings.svg");
    public static FILTER_SUBSCRIBER_TYPE_FREE = new TciMarketplaceV2Filters("FREE", "Free", 1, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER, "ng-ic-filters-free.svg");
    public static FILTER_SUBSCRIBER_TYPE_PAID = new TciMarketplaceV2Filters("PAID", "Paid", 2, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER, "ng-ic-filters-paid.svg");
    // public static FILTER_SUBSCRIBER_TYPE_RECENTLY_ADDED = new TciMarketplaceV2Filters("RECENTLY_ADDED", "Recently Added", 2, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_SUBSCRIBER, "ng-ic-filters-recently-added.svg");
    public static FILTER_PROVIDER_TYPE_PENDING_APPROVAL = new TciMarketplaceV2Filters("PENDING_APPROVAL", "Pending Approval", 0, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER, "");
    public static FILTER_PROVIDER_TYPE_PUBLIC_VISIBILITY = new TciMarketplaceV2Filters("PUBLIC_VISIBILITY", "Public Visibility", 1, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER, "");
    public static FILTER_PROVIDER_TYPE_PRIVATE_VISIBILITY = new TciMarketplaceV2Filters("PRIVATE_VISIBILITY", "Private Visibility", 2, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER, "");
    public static FILTER_PROVIDER_TYPE_PENDING_ACCESS_REQUEST = new TciMarketplaceV2Filters("ACCESS_REQUESTS_PENDING", "Access Requests Pending", 3, TCIMarketplaceV2FilterCategory.FILTER_CATEGORY_PROVIDER, "");


    private static allItems = [
        TciMarketplaceV2Filters.FILTER_LISTING_TYPE_CONNECTOR,
        TciMarketplaceV2Filters.FILTER_LISTING_TYPE_APPLICATION,
        TciMarketplaceV2Filters.FILTER_LISTING_TYPE_ACCELERATOR,
        TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_DEVELOP,
        TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_CONNECT,
        TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_INTEGRATE,
        TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_OTHER,
        TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_ORG,
        TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_FREE,
        TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_PAID,
        // TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_RECENTLY_ADDED,
        TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_APPROVAL,
        TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PUBLIC_VISIBILITY,
        TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PRIVATE_VISIBILITY,
        TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_ACCESS_REQUEST,
    ];

    constructor(private _id: string, private _label: string, index: number, private _category: TCIMarketplaceV2FilterCategory, private _iconUrl: string) {
    }

    public static getFilters(category?: TCIMarketplaceV2FilterCategory): TciMarketplaceV2Filters[] {
        return TciMarketplaceV2Filters.allItems.filter(val => lodash.isEqual(val._category, category));
    }

    public static getAllCategories(): TCIMarketplaceV2FilterCategory[] {
        return TCIMarketplaceV2FilterCategory.getAllCategories();
    }

    public static fromId(id: string): TciMarketplaceV2Filters {
        switch (id) {
            case TciMarketplaceV2Filters.FILTER_LISTING_TYPE_CONNECTOR.id:
                return TciMarketplaceV2Filters.FILTER_LISTING_TYPE_CONNECTOR;
            case TciMarketplaceV2Filters.FILTER_LISTING_TYPE_APPLICATION.id:
                return TciMarketplaceV2Filters.FILTER_LISTING_TYPE_APPLICATION;
            case TciMarketplaceV2Filters.FILTER_LISTING_TYPE_ACCELERATOR.id:
                return TciMarketplaceV2Filters.FILTER_LISTING_TYPE_ACCELERATOR;
            case TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_DEVELOP.id:
                return TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_DEVELOP;
            case TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_CONNECT.id:
                return TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_CONNECT;
            case TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_INTEGRATE.id:
                return TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_INTEGRATE;
            case TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_OTHER.id:
                return TciMarketplaceV2Filters.FILTER_CAPABILITY_TYPE_OTHER;
            case TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_ORG.id:
                return TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_ORG;
            case TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_FREE.id:
                return TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_FREE;
            case TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_PAID.id:
                return TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_PAID;
            // case TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_RECENTLY_ADDED.id:
            //     return TciMarketplaceV2Filters.FILTER_SUBSCRIBER_TYPE_RECENTLY_ADDED;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_APPROVAL.id:
                return TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_APPROVAL;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PUBLIC_VISIBILITY.id:
                return TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PUBLIC_VISIBILITY;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PRIVATE_VISIBILITY.id:
                return TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PRIVATE_VISIBILITY;
            case TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_ACCESS_REQUEST.id:
                return TciMarketplaceV2Filters.FILTER_PROVIDER_TYPE_PENDING_ACCESS_REQUEST;
        }
    }

    get id() {
        return this._id;
    }

    get iconUrl(): string {
        return this._iconUrl;
    }

    get label() {
        return this._label;
    }

    getCategory(): TCIMarketplaceV2FilterCategory {
        return this._category;
    }
}

/*************************************************************************************************/
/*                        TCI Marketplace Menus                                                  */
/*************************************************************************************************/
/**
 * Enumeration of TCI menu types
 */
export const TCI_MARKETPLACE_LISTING_MENU_TYPE: ITCIOptionType<string> = {
    POPUP: "popup",
    MODAL: "modal"
};
export type TCI_MARKETPLACE_LISTING_MENU_TYPE = keyof typeof TCI_MARKETPLACE_LISTING_MENU_TYPE;

/**
 * class TCIMarketplaceV2DetailsMenu
 */
// tslint:disable:max-line-length
export class TCIMarketplaceV2DetailsMenu
    extends AbstractTCITenantComponentOptions<TCI_MARKETPLACE_LISTING_MENU_TYPE>
    implements ITCITenantComponentOptions<TCI_MARKETPLACE_LISTING_MENU_TYPE>,
    ITCITenantModalOptions {
    public static TCI_MENU_MKP_EDIT_DETAILS = new TCIMarketplaceV2DetailsMenu("Edit Details", TCITenantSelectorType.TCI_MKP_EDIT_DETAILS, 1, "details", [], TCI_MARKETPLACE_LISTING_MENU_TYPE.MODAL, TCITenantModalType.TCI_MODAL_MARKETPLACE);
    public static TCI_MENU_MKP_MANAGE_ACCESS = new TCIMarketplaceV2DetailsMenu("Manage Access For Orgs", TCITenantSelectorType.TCI_MENU_MKP_MANAGE_ACCESS, 2, "manage_access", [], TCI_MARKETPLACE_LISTING_MENU_TYPE.MODAL, TCITenantModalType.TCI_MODAL_MARKETPLACE_MANAGE_ACCESS);

    public static TCI_MENU_MKP_CHANGE_VISIBILITY_PUBLIC = new TCIMarketplaceV2DetailsMenu("Change Visibility to Public", TCITenantSelectorType.TCI_MENU_MKP_CHANGE_VISIBILITY_PUBLIC, 2, "change_visibility_public", [], TCI_MARKETPLACE_LISTING_MENU_TYPE.POPUP, TCITenantModalType.TCI_MODAL_MEDIUM);
    public static TCI_MENU_MKP_CHANGE_VISIBILITY_PRIVATE = new TCIMarketplaceV2DetailsMenu("Change Visibility to Private", TCITenantSelectorType.TCI_MENU_MKP_CHANGE_VISIBILITY_PRIVATE, 2, "change_visibility_private", [], TCI_MARKETPLACE_LISTING_MENU_TYPE.POPUP, TCITenantModalType.TCI_MODAL_MEDIUM);
    public static TCI_MENU_MKP_WITHDRAW_PENDING_LISTING = new TCIMarketplaceV2DetailsMenu("Withdraw pending listing", TCITenantSelectorType.TCI_MENU_MKP_WITHDRAW_PENDING_LISTING, 4, "withdraw_pending_listing", [], TCI_MARKETPLACE_LISTING_MENU_TYPE.POPUP, TCITenantModalType.TCI_MODAL_MEDIUM);
    public static TCI_MENU_MKP_DELETE_LISTING = new TCIMarketplaceV2DetailsMenu("Delete listing", TCITenantSelectorType.TCI_MENU_MKP_DELETE_LISTING, 5, "delete_listing", [], TCI_MARKETPLACE_LISTING_MENU_TYPE.POPUP, TCITenantModalType.TCI_MODAL_MEDIUM);

    private static allItems = [
        TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_EDIT_DETAILS,
        TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_MANAGE_ACCESS,
        TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_CHANGE_VISIBILITY_PUBLIC,
        TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_CHANGE_VISIBILITY_PRIVATE,
        TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_WITHDRAW_PENDING_LISTING,
        TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_DELETE_LISTING
    ];

    /**
     * Gets All menu items
     * @param menuType
     * @return @ITCITenantMenu[]
     */
    public static getAllMenuItems(menuType?: TCI_MARKETPLACE_LISTING_MENU_TYPE): ITCITenantComponentOptions<TCI_MARKETPLACE_LISTING_MENU_TYPE>[] {
        if (menuType) {
            return TCIMarketplaceV2DetailsMenu.allItems.filter((v) => {
                return v.getOptionType() === menuType;
            });
        }
        return TCIMarketplaceV2DetailsMenu.allItems;
    }

    /**
     * constructor
     * @param menuOption
     * @param componentSelectorType
     * @param position
     * @param routeId
     * @param tenantAppTypes
     * @param menuType
     * @param modalType
     */
    constructor(menuOption: string,
        componentSelectorType: TCITenantSelectorType,
        position: number,
        routeId: string,
        tenantAppTypes: TCITenantAppType[],
        menuType: TCI_MARKETPLACE_LISTING_MENU_TYPE = TCI_MARKETPLACE_LISTING_MENU_TYPE.MAIN_MENU,
        private modalType: ITCITenantModalType = TCITenantModalType.TCI_MODAL_MEDIUM,
        private enabled = false) {
        super(menuOption, componentSelectorType, position, menuType, routeId, tenantAppTypes, null);
    }

    getModalType(): ITCITenantModalType {
        return this.modalType;
    }

    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
    }

    isEnabled(): boolean {
        return this.enabled;
    }
}

/*************************************************************************************************/
/*                        TCI Marketplace  Editor / Menu Componenet I/O                         */

/*************************************************************************************************/
/**
 * interface ITCIMarketplaceV2DetailInput
 */
export interface ITCIMarketplaceV2MenuInput {
    /**
     * Editor modes
     */
    editorMode: TCI_MARKETPLACE_LISTING_EDITOR_MODE | string;
    /**
     * During create listing editorMode = TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE
     * listing = ITCIMarketplaceListingMetaData
     * During edit listing editorMode = TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT
     * listing = ITCIMarketplaceListing
     */
    listing: ITCIMarketplaceListing | ITCIMarketplaceListingMetaData;
    /**
     * component option type
     */
    componentOption?: ITCITenantComponentOptions<TCI_MARKETPLACE_LISTING_MENU_TYPE>;
    /**
     * If flogo provides this return url then Marketplace editor will navigate to Flogo extensions
     * If it is not provided then the editor will stay or navigate to main marketplace url.
     */
    returnUrl?: string;
}

/**
 * interface ITCIMarketplaceV2DetailOutput
 */
export interface ITCIMarketplaceV2MenuOutput<T> {
    /**
     * Modal is closing
     */
    closeModal?: boolean;
    /**
     * type of submit action
     */
    submitActionType?: TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION;
    /**
     * modal is cancelled
     */
    cancel?: boolean;
    /**
     * refresh parent
     */
    refresh?: boolean;
    /**
     * context data during cancel/submit
     */
    result?: T;
    /**
     * success or error
     */
    success?: boolean;
    /**
     * Error information from TCI
     */
    error?: { message: string, statusMessage: string };
}

/*************************************************************************************************/
/*                        TCI Marketplace  Notification Subject Types                           */
/*************************************************************************************************/
export const TCI_MARKETPLACE_NOTIFICATION_TYPE = strEnum(["Create", "Update", "Delete", "Access", "Refresh"]);
export type TCI_MARKETPLACE_NOTIFICATION_TYPE = keyof typeof TCI_MARKETPLACE_NOTIFICATION_TYPE;

/*************************************************************************************************/
/*                        TCI Marketplace  Notification Subject Output                           */

/*************************************************************************************************/
export interface ITCIMarketplaceChangeNotification<T> {
    /**
     * Type of notification
     */
    type: TCI_MARKETPLACE_NOTIFICATION_TYPE;

    /**
     * Listing asset that have changed
     */
    listing: T;
}
